// File: src/pages/Employee/EmployeeBankDetails.js
import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress, Container } from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const EmployeeBankDetails = () => {
    const { user } = useAuth();
    const [details, setDetails] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchDetails = useCallback(async () => {
        if (!user?.employeeId) return;
        setLoading(true);
        setError('');
        try {
            // API Endpoint: GET /bank-details/my-details
            const response = await api.get('/bank-details/my-details');
            setDetails(response.data);
            setFormData(response.data);
            setIsSubmitted(true);
        } catch (err) {
            // Check for 404 or 204 status indicating no existing record
            if (err.response && (err.response.status === 404 || err.response.status === 204)) {
                setDetails(null);
                setIsSubmitted(false);
            } else {
                setError(err.response?.data?.message || 'Failed to fetch bank details.');
            }
        } finally {
            setLoading(false);
        }
    }, [user]); 

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]); 

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmission = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // API Endpoint: POST /bank-details/my-details
            await api.post('/bank-details/my-details', formData);
            setSuccess('Bank details submitted successfully! They are now pending HR verification.');
            fetchDetails(); 
        } catch (err) {
            setError(err.response?.data?.message || 'Submission failed. Please check inputs.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIndicator = () => {
        if (!details) return null;

        if (details.verifiedByHr) {
            return (
                <Alert icon={<CheckCircle fontSize="inherit" />} severity="success" sx={{ mb: 3 }}>
                    <Typography variant="h6">Verified by HR</Typography>
                    <Typography variant="body2">**HR Remark:** {details.hrRemark || 'Verified successfully.'}</Typography>
                </Alert>
            );
        } else {
            return (
                <Alert icon={<Warning fontSize="inherit" />} severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="h6">Pending HR Review</Typography>
                    <Typography variant="body2">**Status:** {details.hrRemark || 'Awaiting HR verification.'}</Typography>
                    <Typography variant="body2">Note: Only HR can edit or verify these details.</Typography>
                </Alert>
            );
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    }

    if (isSubmitted) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom>My Bank Details</Typography>
                {getStatusIndicator()}
                <Paper sx={{ p: 3 }}>
                    <TextField fullWidth margin="normal" label="Bank Name" value={details.bankName} InputProps={{ readOnly: true }} />
                    <TextField fullWidth margin="normal" label="Account Number" value={details.accountNumber} InputProps={{ readOnly: true }} />
                    <TextField fullWidth margin="normal" label="IFSC Code" value={details.ifsc} InputProps={{ readOnly: true }} />
                    <TextField fullWidth margin="normal" label="Branch" value={details.branch} InputProps={{ readOnly: true }} />
                    <TextField fullWidth margin="normal" label="UPI ID (Optional)" value={details.upi} InputProps={{ readOnly: true }} />
                </Paper>
            </Box>
        );
    }

    return (
        <Container component="main" maxWidth="md">
            <Paper elevation={3} sx={{ mt: 4, p: 4 }}>
                <Typography variant="h4" gutterBottom>Initial Bank Details Submission</Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Please enter your bank details carefully. You can only submit once. All subsequent edits must be done by HR.
                </Alert>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Box component="form" onSubmit={handleSubmission} sx={{ mt: 1 }}>
                    <TextField required fullWidth margin="normal" name="bankName" label="Bank Name" onChange={handleFormChange} />
                    <TextField required fullWidth margin="normal" name="accountNumber" label="Account Number" onChange={handleFormChange} />
                    <TextField required fullWidth margin="normal" name="ifsc" label="IFSC Code" onChange={handleFormChange} />
                    <TextField fullWidth margin="normal" name="branch" label="Branch" onChange={handleFormChange} />
                    <TextField fullWidth margin="normal" name="upi" label="UPI ID (Optional)" onChange={handleFormChange} />

                    <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Details for HR Verification'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default EmployeeBankDetails;